import { JSX, useEffect, useState } from "react";
import Button from "../Button/Button";
import "./NetworkError.scss";

export type NetworkErrorVariant =
  | "offline"
  | "timeout"
  | "server-error"
  | "generic-network-error";

interface NetworkErrorProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
  onBack?: () => void;
  loading?: boolean;
  fullScreen?: boolean;
  variant?: NetworkErrorVariant;
  retryLabel?: string;
  backLabel?: string;
}

const VARIANT_CONTENT: Record<
  NetworkErrorVariant,
  { title: string; description: string; code?: string }
> = {
  offline: {
    title: "You're Offline",
    description:
      "No internet connection detected. Please check your network settings and try again.",
    code: "ERR_INTERNET_DISCONNECTED",
  },
  timeout: {
    title: "Request Timed Out",
    description:
      "The server took too long to respond. This may be a temporary issue — please try again.",
    code: "ERR_TIMED_OUT",
  },
  "server-error": {
    title: "Server Unavailable",
    description:
      "We're having trouble reaching our servers. Our team has been notified. Please try again shortly.",
    code: "ERR_SERVER_UNREACHABLE",
  },
  "generic-network-error": {
    title: "Something went wrong",
    description: "We're unable to connect right now. Please try again later.",
    code: "ERR_NETWORK",
  },
};

// ── SVG Illustrations ──────────────────────────────────────────────────────────

const OfflineIcon = () => (
  <svg
    className="ne-illustration"
    viewBox="0 0 120 100"
    fill="none"
    aria-hidden="true"
  >
    <ellipse cx="60" cy="90" rx="42" ry="6" className="ne-shadow" />
    <path
      d="M20 42C32 28 48 20 60 20C72 20 88 28 100 42"
      className="ne-arc ne-arc--1"
      strokeLinecap="round"
    />
    <path
      d="M32 54C40 44 50 38 60 38C70 38 80 44 88 54"
      className="ne-arc ne-arc--2"
      strokeLinecap="round"
    />
    <path
      d="M44 66C49 59 54 55 60 55C66 55 71 59 76 66"
      className="ne-arc ne-arc--3"
      strokeLinecap="round"
    />
    <circle cx="60" cy="78" r="4" className="ne-dot" />
    <line
      x1="15"
      y1="15"
      x2="105"
      y2="85"
      className="ne-slash"
      strokeLinecap="round"
    />
  </svg>
);

const TimeoutIcon = () => (
  <svg
    className="ne-illustration"
    viewBox="0 0 120 100"
    fill="none"
    aria-hidden="true"
  >
    <ellipse cx="60" cy="90" rx="42" ry="6" className="ne-shadow" />
    <circle cx="60" cy="48" r="32" className="ne-circle" />
    <line
      x1="60"
      y1="28"
      x2="60"
      y2="50"
      className="ne-hand ne-hand--minute"
      strokeLinecap="round"
    />
    <line
      x1="60"
      y1="48"
      x2="74"
      y2="58"
      className="ne-hand ne-hand--hour"
      strokeLinecap="round"
    />
    <circle cx="60" cy="48" r="3" className="ne-dot" />
    <path
      d="M47 16 L60 8 L73 16"
      className="ne-cap"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ServerErrorIcon = () => (
  <svg
    className="ne-illustration"
    viewBox="0 0 120 100"
    fill="none"
    aria-hidden="true"
  >
    <ellipse cx="60" cy="90" rx="42" ry="6" className="ne-shadow" />
    <rect x="24" y="20" width="72" height="22" rx="4" className="ne-server" />
    <rect x="24" y="48" width="72" height="22" rx="4" className="ne-server" />
    <circle cx="88" cy="31" r="4" className="ne-led ne-led--red" />
    <circle cx="78" cy="31" r="4" className="ne-led ne-led--amber" />
    <circle cx="88" cy="59" r="4" className="ne-led ne-led--red" />
    <circle cx="78" cy="59" r="4" className="ne-led ne-led--amber" />
    <rect x="30" y="26" width="30" height="10" rx="2" className="ne-bar" />
    <rect x="30" y="54" width="20" height="10" rx="2" className="ne-bar" />
    <path
      d="M60 68 L60 80"
      className="ne-pipe"
      strokeLinecap="round"
      strokeDasharray="3 3"
    />
    <circle cx="60" cy="84" r="4" className="ne-dot ne-dot--red" />
  </svg>
);

const GenericNetworkIcon = () => (
  <svg
    className="ne-illustration"
    viewBox="0 0 120 100"
    fill="none"
    aria-hidden="true"
  >
    <ellipse cx="60" cy="90" rx="42" ry="6" className="ne-shadow" />
    <circle cx="60" cy="48" r="28" className="ne-globe-circle" />
    <ellipse cx="60" cy="48" rx="12" ry="28" className="ne-globe-ellipse" />
    <line x1="32" y1="48" x2="88" y2="48" className="ne-globe-line" />
    <line x1="36" y1="34" x2="84" y2="34" className="ne-globe-line" />
    <line x1="36" y1="62" x2="84" y2="62" className="ne-globe-line" />
    <circle cx="60" cy="48" r="4" className="ne-dot ne-dot--red" />
    <line
      x1="52"
      y1="40"
      x2="68"
      y2="56"
      className="ne-x ne-x--1"
      strokeLinecap="round"
    />
    <line
      x1="68"
      y1="40"
      x2="52"
      y2="56"
      className="ne-x ne-x--2"
      strokeLinecap="round"
    />
  </svg>
);

const VARIANT_ICONS: Record<NetworkErrorVariant, () => JSX.Element> = {
  offline: OfflineIcon,
  timeout: TimeoutIcon,
  "server-error": ServerErrorIcon,
  "generic-network-error": GenericNetworkIcon,
};

// ── Component ──────────────────────────────────────────────────────────────────

const NetworkError = ({
  title,
  description,
  onRetry,
  onBack,
  loading = false,
  fullScreen = true,
  variant,
  retryLabel = "Retry",
  backLabel = "Back to Dashboard",
}: NetworkErrorProps) => {
  const [isOnline, setIsOnline] = useState(window.navigator.onLine);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const resolvedVariant: NetworkErrorVariant =
    variant ?? (!isOnline ? "offline" : "generic-network-error");

  const content = VARIANT_CONTENT[resolvedVariant];
  const Icon = VARIANT_ICONS[resolvedVariant];

  const resolvedTitle = title ?? content.title;
  const resolvedDescription = description ?? content.description;

  const handleRetry = () => {
    setRetryCount((c) => c + 1);
    onRetry?.();
  };

  return (
    <div
      className={`networkError${fullScreen ? " networkError--fullScreen" : ""}`}
      role="alert"
      aria-live="assertive"
      aria-label={`${resolvedTitle}: ${resolvedDescription}`}
    >
      <div className="ne-card">
        <div className="ne-iconWrap" aria-hidden="true">
          <Icon />
        </div>

        <div className="ne-content">
          <div className={`ne-badge ne-badge--${resolvedVariant}`}>
            {resolvedVariant === "offline"
              ? "Offline"
              : resolvedVariant === "timeout"
                ? "Timed Out"
                : resolvedVariant === "server-error"
                  ? "Server Error"
                  : "Server Error"}
          </div>

          <h1 className="ne-title">{resolvedTitle}</h1>
          <p className="ne-description">{resolvedDescription}</p>

          {!isOnline && (
            <div className="ne-statusPill ne-statusPill--offline" role="status">
              <span className="ne-statusDot" />
              Something went wrong
            </div>
          )}

          {retryCount > 2 && (
            <p className="ne-hint">
              Still not working? Try refreshing the page or contact support.
            </p>
          )}
        </div>

        <div className="ne-actions">
          {onRetry && (
            <Button
              className="ne-btn ne-btn--primary"
              onClick={handleRetry}
              disabled={loading}
              aria-label={loading ? "Retrying connection…" : retryLabel}
              type="button"
            >
              {loading ? (
                <>
                  <span className="ne-spinner" aria-hidden="true" />
                  Retrying…
                </>
              ) : (
                <>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M13.65 2.35A8 8 0 1 0 14 8"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                    <path
                      d="M14 2v4h-4"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {retryLabel}
                </>
              )}
            </Button>
          )}

          {onBack && (
            <Button
              className="ne-btn ne-btn--secondary"
              onClick={onBack}
              disabled={loading}
              aria-label={backLabel}
              type="button"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M10 12L6 8l4-4"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {backLabel}
            </Button>
          )}
        </div>

        {/* {content.code && (
          <p
            className="ne-errorCode"
            aria-label={`Error code: ${content.code}`}
          >
            {content.code}
          </p>
        )} */}
      </div>
    </div>
  );
};

export default NetworkError;
